import type { FunctionComponent, ReactElement } from 'react'

import { FlowPage } from '@monthio/design-system'
import { LoadingScreen, notificationService } from '@monthio/react-components'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type {
	ExtraOrdinaryItemGroupType,
	SavingsCapacityExtraordinaryItemGroupDto,
	TransactionOutputApplicantTransactionDto,
} from './SavingsCapacity.types'

import { useSavingsCapacityModule } from '.'
import { ExtraordinaryItemsGroupPage } from './Components/ExtraordinaryItemsGroupPage'
import { ExtraordinaryItemsGroupSelectorPage } from './Components/ExtraordinaryItemsGroupSelectorPage'
import { SavingsCapacityPageIntro } from './Components/SavingsCapacityPageIntro'
import { extraOrdinaryItemGroups } from './SavingsCapacity.types'

export type SavingCapacityPages =
	| 'intro'
	| 'extraordinary-items-categories'
	| 'extraordinary-items-transactions'

const AMOUNT_FLOOR = -5000

const mapTransactionsToCategories = (
	response: TransactionOutputApplicantTransactionDto[],
	selectedCategories: ExtraOrdinaryItemGroupType[],
	initialCategoryTransactions: Record<
		ExtraOrdinaryItemGroupType,
		TransactionOutputApplicantTransactionDto[]
	>,
) => {
	return response.reduce(
		(
			acc: Record<
				ExtraOrdinaryItemGroupType,
				TransactionOutputApplicantTransactionDto[]
			>,
			transaction,
		) => {
			const groupKeys = Object.entries(extraOrdinaryItemGroups).filter(
				([_, categoryIds]) =>
					(categoryIds as readonly number[]).includes(transaction.category.id),
			)

			for (const [groupKey, _] of groupKeys) {
				if (
					selectedCategories.includes(groupKey as ExtraOrdinaryItemGroupType)
				) {
					acc[groupKey as ExtraOrdinaryItemGroupType].push(transaction)
				}
			}

			return acc
		},
		initialCategoryTransactions,
	)
}

type CategoryTransactionsRecord = Record<
	ExtraOrdinaryItemGroupType,
	TransactionOutputApplicantTransactionDto[]
>

export const SavingsCapacity: FunctionComponent = () => {
	const [currentPage, setCurrentPage] = useState<
		| 'intro'
		| 'extraordinary-items-categories'
		| 'extraordinary-items-transactions'
	>('intro')
	const { t } = useTranslation()
	const { finish, config } = useSavingsCapacityModule()
	const [selectedGroupTypes, setSelectedGroupTypes] = useState<
		ExtraOrdinaryItemGroupType[]
	>([])
	const [categoryToTransactions, setCategoryToTransactions] =
		useState<CategoryTransactionsRecord>(
			() => ({}) as CategoryTransactionsRecord,
		)
	const [reducedCategoryToTransactions, setReducedCategoryToTransactions] =
		useState<CategoryTransactionsRecord>(
			() => ({}) as CategoryTransactionsRecord,
		)
	const [extraordinaryItemGroups, setExtraordinaryItemGroups] = useState<
		SavingsCapacityExtraordinaryItemGroupDto[]
	>([])
	const [selectedExtraordinaryItems, setSelectedExtraordinaryItems] = useState(
		() => new Map<string, string>(),
	)
	const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0)
	const [isFetchingData, setIsFetchingData] = useState(false)
	const [hasFetchingDataFailed, setHasFetchingDataFailed] = useState(false)

	useEffect(() => {
		const _selectedExtraordinaryItems = extraordinaryItemGroups.reduce(
			(acc, group) => {
				group.items.forEach((item) => {
					if (item.type === 'transaction') {
						acc.set(item.transactionId, group.type)
					}
				})
				return acc
			},
			new Map<string, string>(),
		)
		setSelectedExtraordinaryItems(_selectedExtraordinaryItems)
	}, [extraordinaryItemGroups])

	useEffect(() => {
		const _reducedCategoryToTransactions = Object.entries(
			categoryToTransactions,
		).reduce((acc, [groupType, transactions]) => {
			acc[groupType as ExtraOrdinaryItemGroupType] = transactions.filter(
				(item: TransactionOutputApplicantTransactionDto) => {
					const selectedGroupType = selectedExtraordinaryItems.get(item.id)
					return (
						selectedGroupType == undefined || selectedGroupType == groupType
					)
				},
			)
			return acc
		}, {} as CategoryTransactionsRecord)
		setReducedCategoryToTransactions(_reducedCategoryToTransactions)
	}, [selectedExtraordinaryItems, categoryToTransactions])

	const finishModule = useCallback(
		async (
			_extraordinaryItemGroups: SavingsCapacityExtraordinaryItemGroupDto[],
		) => {
			try {
				if (_extraordinaryItemGroups.length > 0) {
					await finish(_extraordinaryItemGroups)
				}
			} catch (error) {
				console.error(error)
				notificationService.error(
					t('savingsCapacityPage-submitExtraordinaryItemGroups-error'),
				)
				throw error
			}
		},
		[history, t],
	)

	const goToNextPage = useCallback(() => {
		setCurrentPage((prevPage) => {
			switch (prevPage) {
				case 'intro':
					return 'extraordinary-items-categories'
				case 'extraordinary-items-categories':
					return 'extraordinary-items-transactions'
				case 'extraordinary-items-transactions':
					return prevPage
				default:
					throw new Error(`Unhandled page transition from: ${prevPage}`)
			}
		})
	}, [])

	const goToPreviousPage = useCallback(() => {
		setCurrentPage((prevPage) => {
			switch (prevPage) {
				case 'extraordinary-items-categories':
					return 'intro'
				case 'extraordinary-items-transactions':
					return 'extraordinary-items-categories'

				default:
					return prevPage
			}
		})
	}, [])

	const handleExtraordinaryItemGroupNext = useCallback(
		(
			savingsCapacityExtraordinaryItemGroup: SavingsCapacityExtraordinaryItemGroupDto,
		) => {
			const newExtraordinaryItemsGroups = [
				...extraordinaryItemGroups.filter(
					(group) => group.type !== savingsCapacityExtraordinaryItemGroup.type,
				),
				savingsCapacityExtraordinaryItemGroup,
			]
			setExtraordinaryItemGroups(newExtraordinaryItemsGroups)

			if (currentCategoryIndex < selectedGroupTypes.length - 1) {
				setCurrentCategoryIndex((prev) => prev + 1)
			} else if (currentCategoryIndex === selectedGroupTypes.length - 1) {
				finishModule(newExtraordinaryItemsGroups)
			} else {
				goToNextPage()
			}
		},
		[
			currentCategoryIndex,
			extraordinaryItemGroups,
			finishModule,
			goToNextPage,
			selectedGroupTypes.length,
		],
	)

	const handleExtreaordinaryItemGroupPrevious = useCallback(() => {
		if (currentCategoryIndex > 0) {
			setCurrentCategoryIndex((prev) => prev - 1)
		} else {
			goToPreviousPage()
		}
	}, [currentCategoryIndex, goToPreviousPage])

	const handleCategoriesNext = useCallback(
		(categories: ExtraOrdinaryItemGroupType[]) => {
			setSelectedGroupTypes(categories)
			categories.length > 0 ? goToNextPage() : finishModule([])
		},
		[finishModule, goToNextPage, setSelectedGroupTypes],
	)

	useEffect(() => {
		if (currentPage !== 'extraordinary-items-transactions') return

		const fetchTransactions = async () => {
			setIsFetchingData(true)
			const categoryIds = selectedGroupTypes.reduce(
				(acc, category) => [...acc, ...extraOrdinaryItemGroups[category]],
				new Array<number>(),
			)
			const uniqueCategoryIds = [...new Set(categoryIds)]

			try {
				const response = await config.getSessionTransactionsByCategories(
					uniqueCategoryIds,
					AMOUNT_FLOOR,
				)

				const initialCategoryTransactions = selectedGroupTypes.reduce(
					(acc, category) => ({
						...acc,
						[category]: [],
					}),
					{} as Record<
						ExtraOrdinaryItemGroupType,
						TransactionOutputApplicantTransactionDto[]
					>,
				)

				const mappedTransactions = mapTransactionsToCategories(
					response.collection,
					selectedGroupTypes,
					initialCategoryTransactions,
				)

				setCategoryToTransactions(mappedTransactions)
			} catch (_error) {
				setHasFetchingDataFailed(true)
			} finally {
				setIsFetchingData(false)
			}
		}

		fetchTransactions()
	}, [currentPage, selectedGroupTypes, config])

	if (hasFetchingDataFailed) {
		notificationService.error(
			t('savingsCapacityPage-fetchingTransactions-error'),
		)
		goToPreviousPage()
	}

	const pages = useMemo(
		() =>
			Object.fromEntries(
				selectedGroupTypes.map((groupType) => [
					groupType,
					<ExtraordinaryItemsGroupPage
						key={groupType}
						onNext={handleExtraordinaryItemGroupNext}
						onPrevious={handleExtreaordinaryItemGroupPrevious}
						transactions={reducedCategoryToTransactions[groupType] ?? []}
						extraordinaryGroupType={groupType}
						initialItems={extraordinaryItemGroups.find(
							(group) => group.type === groupType,
						)}
					/>,
				]),
			) as Record<ExtraOrdinaryItemGroupType, ReactElement>,
		[
			extraordinaryItemGroups,
			handleExtraordinaryItemGroupNext,
			handleExtreaordinaryItemGroupPrevious,
			reducedCategoryToTransactions,
			selectedGroupTypes,
		],
	)

	if (isFetchingData) {
		return (
			<FlowPage>
				<FlowPage.Content $isCentered>
					<LoadingScreen />
				</FlowPage.Content>
			</FlowPage>
		)
	}

	switch (currentPage) {
		case 'intro':
			return <SavingsCapacityPageIntro onNext={goToNextPage} />
		case 'extraordinary-items-categories':
			return (
				<ExtraordinaryItemsGroupSelectorPage
					onNext={handleCategoriesNext}
					initialSelectedGroupTypes={selectedGroupTypes}
				/>
			)
		case 'extraordinary-items-transactions':
			const extraordinaryGroupType = selectedGroupTypes[currentCategoryIndex]!

			return pages[extraordinaryGroupType]

		default:
			throw new Error(`Unhandled page: ${currentPage}`)
	}
}
